import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Tab,
  Tabs,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import CreateOrganization from '../../components/organization/CreateOrganization';
import ManageMembers from '../../components/organization/ManageMembers';
import AcceptInvitation from '../../components/organization/AcceptInvitation';
import { CurrencySettings } from '../../components/organization/CurrencySettings';
import FleetManagement from '../../components/organization/FleetManagement';
import { Organization } from '../../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`organization-tabpanel-${index}`}
      aria-labelledby={`organization-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
};

const OrganizationDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openInvitationDialog, setOpenInvitationDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const { user } = useAuth();

  const loadOrganization = async () => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    try {
      console.log('Loading organization...', { organizationId: user.organizationId });
      const org = await organizationService.getOrganization(user.organizationId);
      
      if (!org) {
        console.warn('Organization not found:', user.organizationId);
        setError('Organization not found');
        setOrganization(null);
        return;
      }

      console.log('Organization loaded:', org);
      setOrganization(org);

      const adminStatus = await organizationService.isUserAdmin(org.id, user.id);
      console.log('Admin status checked:', { isAdmin: adminStatus });
      setIsAdmin(adminStatus);
    } catch (err) {
      console.error('Error loading organization:', err);
      setError('Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadOrganization();
      if (!user.organizationId) {
        setOpenInvitationDialog(true);
      }
    }
  }, [user]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateSuccess = async () => {
    try {
      await loadOrganization();
      setSnackbar({
        open: true,
        message: t('organization.createSuccess'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Error after organization creation:', err);
      setSnackbar({
        open: true,
        message: t('organization.loadError'),
        severity: 'error',
      });
    }
  };

  const handleInvitationComplete = async () => {
    setOpenInvitationDialog(false);
    await loadOrganization();
  };

  const handleDeleteOrganization = async () => {
    if (!organization) return;

    if (window.confirm(t('organization.deleteConfirm'))) {
      try {
        setLoading(true);
        await organizationService.deleteOrganization(organization.id);
        setOrganization(null);
        setIsAdmin(false);
        setSnackbar({
          open: true,
          message: t('organization.deleteSuccess'),
          severity: 'success',
        });
      } catch (err) {
        console.error('Error deleting organization:', err);
        setSnackbar({
          open: true,
          message: t('organization.deleteError'),
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if ((!organization && !loading) || error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Box sx={{ typography: 'h5', mb: 2 }}>
            {t('organization.welcome')}
          </Box>
          <Box sx={{ typography: 'body1', color: 'text.secondary', mb: 3 }}>
            {t('organization.noOrganization')}
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCreateDialog(true)}
          >
            {t('organization.create')}
          </Button>

          <CreateOrganization
            open={openCreateDialog}
            onClose={() => setOpenCreateDialog(false)}
            onSuccess={handleCreateSuccess}
          />

          {user && (
            <AcceptInvitation
              open={openInvitationDialog}
              onClose={() => setOpenInvitationDialog(false)}
              onSuccess={handleInvitationComplete}
              email={user.email}
            />
          )}

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper sx={{ mt: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t('organization.overview')} />
            {isAdmin && <Tab label={t('organization.members')} />}
            {isAdmin && <Tab label={t('organization.fleets')} />}
            {isAdmin && <Tab label={t('organization.settings')} />}
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ typography: 'h5', mb: 2 }}>
            {organization?.name}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ typography: 'body1', color: 'text.secondary', mb: 1 }}>
              {t('organization.details')}
            </Box>
            <Box sx={{ typography: 'body2', mb: 1 }}>
              {t('organization.created')}: {organization?.createdAt?.toDate().toLocaleDateString()}
            </Box>
            {organization?.updatedAt && (
              <Box sx={{ typography: 'body2' }}>
                {t('organization.lastUpdated')}: {organization.updatedAt.toDate().toLocaleDateString()}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {isAdmin && organization && (
            <ManageMembers organizationId={organization.id} />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {isAdmin && organization && (
            <FleetManagement organizationId={organization.id} />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {isAdmin && organization && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('organization.settings')}
              </Typography>

              {/* Paramètres des devises */}
              <Box sx={{ mb: 4 }}>
                <CurrencySettings organizationId={organization.id} />
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Zone de danger */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" color="error" gutterBottom>
                  {t('organization.dangerZone')}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDeleteOrganization}
                  disabled={loading}
                >
                  {loading ? t('common.deleting') : t('organization.delete')}
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationDashboard;
